import { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarDays, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/money-history`;
const usd = (n) => `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(n)}`;
const tone = (n) => (n > 0 ? 'text-emerald-300' : n < 0 ? 'text-red-400' : 'text-zinc-300');
const PERIODS = [['day', 'Day'], ['week', 'Week'], ['month', 'Month']];

const Total = ({ label, value, cls = 'text-white', testid }) => (
  <div className="min-w-0 rounded-lg border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 text-center">
    <p className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</p>
    <p className={`truncate font-mono text-sm font-black sm:text-base ${cls}`} data-testid={testid}>{value}</p>
  </div>
);

// Top-of-page money history: every ticket since day one, by the night it was placed.
export default function MoneyHistory({ refreshKey }) {
  const [period, setPeriod] = useState('day');
  const [d, setD] = useState(null);
  useEffect(() => {
    const get = () => axios.get(API, { params: { period } }).then((r) => setD(r.data)).catch(() => setD({ rows: [], totals: {} }));
    get();
    window.addEventListener('ticket-money-changed', get);
    return () => window.removeEventListener('ticket-money-changed', get);
  }, [period, refreshKey]);
  const t = d?.totals || {};
  return (
    <section className="space-y-2 rounded-xl border border-zinc-800 bg-zinc-900/70 p-3" data-testid="money-history">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-black"><CalendarDays className="h-4 w-4 text-amber-400" />My money, every night</p>
        <div className="flex rounded-lg border border-zinc-700 p-0.5">
          {PERIODS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`money-period-${k}`}
              className={`rounded-md px-2.5 py-1 text-xs font-bold transition-colors ${period === k ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-white'}`}>{l}</button>
          ))}
        </div>
      </div>
      {!d ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-500" /> : (
        <>
          <div className="grid grid-cols-3 gap-1.5">
            <Total label="Wagered (all-time)" value={usd(t.wagered)} testid="money-all-wagered" />
            <Total label="Won back" value={usd(t.won)} cls="text-emerald-300" testid="money-all-won" />
            <Total label={t.net >= 0 ? 'Up' : 'Down'} value={signed(t.net)} cls={tone(t.net)} testid="money-all-net" />
          </div>
          <p className="text-[10px] text-zinc-500" data-testid="money-since">
            {t.tickets || 0} tickets · {t.wins || 0}W · {t.losses || 0}L{t.open ? ` · ${usd(t.pending)} still pending on ${t.open}` : ''}{d.since ? ` · since ${new Date(`${d.since}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
          </p>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-zinc-800" data-testid="money-history-list">
            {!d.rows.length ? <p className="p-3 text-center text-sm text-zinc-500">No tickets yet.</p> : (
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-zinc-900 text-[9px] uppercase text-zinc-500">
                  <tr className="text-right"><th className="px-2 py-1 text-left font-normal">{PERIODS.find((p) => p[0] === period)[1]}</th><th className="font-normal">Wagered</th><th className="font-normal">Won</th><th className="px-2 font-normal">+/−</th></tr>
                </thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={r.key} className="border-t border-zinc-800 text-right font-mono" data-testid={`money-row-${r.key}`}>
                      <td className="px-2 py-1.5 text-left font-sans">
                        <span className="text-[12px] text-white">{r.label}</span>
                        <span className="block text-[10px] text-zinc-500">{r.tickets} ticket{r.tickets === 1 ? '' : 's'} · {r.wins}W {r.losses}L{r.open ? ` · ${r.open} pending` : ''}</span>
                      </td>
                      <td className="text-zinc-300">{usd(r.wagered)}</td>
                      <td className="text-emerald-300/90">{usd(r.won)}</td>
                      <td className={`px-2 font-bold ${tone(r.net)}`}>{signed(r.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}
